import { isAllowedEmbedSrc } from '../../policy/index.js';
import { escapeHtml, safeUrl } from '../escape.js';
import { editAttr, itemCollection, itemStr, labeledIntro, list, sectionOpen, str } from './helpers.js';
import type { JsonValueT, PatternSectionT } from '../../schema/index.js';

function renderCta(section: PatternSectionT, key: string, item: JsonValueT): string {
  const label = itemStr(item, 'label');
  const url = itemStr(item, 'url');
  if (!label || !url) return '';
  const labelPath = `/${key}/label`;
  return `    <a class="ve-btn ve-contact__cta" href="${safeUrl(url)}"${editAttr(section, labelPath)}>${escapeHtml(label)}</a>`;
}

function visibleContactValue(value: string): string {
  return value.replace(/<[^<>]*>/g, '');
}

function mapSource(mapHtml: string): string {
  const match = /\bsrc\s*=\s*["']([^"']+)["']/i.exec(mapHtml);
  return match?.[1] ?? '';
}

/** contact — contact details, label/url CTAs, and safely displayed map embed source. */
export function renderContact(section: PatternSectionT): string {
  const { fields } = section;
  const heading = str(fields, 'heading');
  const label = str(fields, 'label');
  const primaryCta = renderCta(section, 'primary_cta', fields.primary_cta ?? null);
  const secondaryCta = renderCta(section, 'secondary_cta', fields.secondary_cta ?? null);
  const infoItems = list(fields, 'info_items')
    .map((item, index) => {
      const itemLabel = itemStr(item, 'label');
      const value = itemStr(item, 'value');
      if (!itemLabel && !value) return '';
      const itemPath = `/info_items/${index}`;
      const lines = [`      <div class="ve-contact__info-item"${editAttr(section, itemPath)}>`];
      if (itemLabel) lines.push(`        <dt class="ve-contact__info-label">${escapeHtml(itemLabel)}</dt>`);
      if (value) lines.push(`        <dd class="ve-contact__info-value">${escapeHtml(visibleContactValue(value))}</dd>`);
      lines.push('      </div>');
      return lines.join('\n');
    })
    .filter(Boolean)
    .join('\n');
  const mapHtml = str(fields, 'map_html');
  const parts: string[] = [sectionOpen(section), '  <div class="ve-contact__content">'];
  parts.push(...labeledIntro(section, 'contact', heading, label, 'h2', '    '));
  if (primaryCta || secondaryCta) {
    parts.push('    <div class="ve-contact__actions">');
    if (primaryCta) parts.push(primaryCta);
    if (secondaryCta) parts.push(secondaryCta);
    parts.push('    </div>');
  }
  if (infoItems) parts.push(itemCollection(section, 'dl', 've-contact__info', '/info_items', infoItems, '    '));
  parts.push('  </div>');
  if (mapHtml) {
    const mapUrl = mapSource(mapHtml);
    // Only an allowlisted map/video embed becomes an <iframe>; any other src
    // (arbitrary https, tracking, etc.) degrades to plain text.
    if (mapUrl && isAllowedEmbedSrc(mapUrl)) {
      parts.push(`  <div class="ve-contact__map"${editAttr(section, '/map_html')}><iframe src="${safeUrl(mapUrl)}" title="Map" loading="lazy" referrerpolicy="no-referrer"></iframe></div>`);
    } else {
      parts.push(`  <p class="ve-contact__map"${editAttr(section, '/map_html')}>${escapeHtml(mapHtml)}</p>`);
    }
  }
  parts.push('</section>');
  return parts.join('\n');
}
